import { useState, useEffect } from "react";
import { TextInput, Modal } from "@carbon/react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import HolidayDatePicker from "../../../Components/holidayDatePicker";

// GraphQL for fetching a holiday by ID
const GET_HOLIDAY_BY_ID = gql`
    query GetHolidayById($id: String!) {
        holidayById(id: $id) {
            id
            name
            startDate
            endDate
        }
    }
`;

// GraphQL mutations for editing and deleting holidays
const EDIT_HOLIDAY_MUTATION = gql`
    mutation EditHoliday($id: String!, $name: String, $startDate: String, $endDate: String) {
        editHoliday(id: $id, name: $name, startDate: $startDate, endDate: $endDate) {
            id
            name
            startDate
            endDate
        }
    }
`;

const DELETE_HOLIDAY_MUTATION = gql`
    mutation DeleteHoliday($id: String!) {
        deleteHoliday(id: $id)
    }
`;

export default function HolidayEdit() {
    const navigate = useNavigate();
    const { id } = useParams(); // Get holiday ID from URL
    
    const [holidayName, setHolidayName] = useState("");
    const [dateRange, setDateRange] = useState([]);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [formIsValid, setFormIsValid] = useState(false);

    // Query for the holiday data
    const { loading: holidayLoading, error: holidayError, data: holidayData } = useQuery(GET_HOLIDAY_BY_ID, {
        variables: { id },
        fetchPolicy: 'network-only'
    });

    // Mutations
    const [editHoliday] = useMutation(EDIT_HOLIDAY_MUTATION, {
        onCompleted: () => {
            navigate("/admin/holidays");
        },
        onError: (error) => {
            setError(`Fehler beim Aktualisieren des Feiertags: ${error.message}`);
            setSubmitting(false);
        }
    });

    const [deleteHoliday, { loading: deleteLoading }] = useMutation(DELETE_HOLIDAY_MUTATION, {
        onCompleted: () => {
            navigate("/admin/holidays");
        },
        onError: (error) => {
            setError(`Fehler beim Löschen des Feiertags: ${error.message}`);
            setIsDeleteModalOpen(false);
        }
    });

    // Initialize form with holiday data once loaded
    useEffect(() => {
        if (holidayData && holidayData.holidayById) {
            const { name, startDate, endDate } = holidayData.holidayById;
            setHolidayName(name);
            setDateRange([new Date(startDate), new Date(endDate)]);
        }
    }, [holidayData]);

    // Validate form inputs and update form validity
    useEffect(() => {
        const isNameValid = holidayName.trim() !== '';
        const areDatesValid = dateRange && dateRange[0] && dateRange[1];
        setFormIsValid(isNameValid && areDatesValid);
    }, [holidayName, dateRange]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);

        // Validate form
        if (!holidayName.trim()) {
            setError("Bitte geben Sie einen Namen für den Feiertag ein");
            setSubmitting(false);
            return;
        }

        if (!dateRange[0] || !dateRange[1]) {
            setError("Bitte wählen Sie ein Start- und Enddatum");
            setSubmitting(false);
            return;
        }

        // Ensure we use the selected dates without timezone shifting
        // Create new Date objects with time set to noon to avoid timezone issues
        const adjustDate = (date) => {
            const newDate = new Date(date);
            newDate.setHours(12, 0, 0, 0);
            return newDate.toISOString();
        };
        
        const startDate = adjustDate(dateRange[0]);
        const endDate = adjustDate(dateRange[1]);

        // Submit mutation
        try {
            await editHoliday({
                variables: {
                    id,
                    name: holidayName,
                    startDate,
                    endDate
                }
            });
        } catch (err) {
            setError(`Ein Fehler ist aufgetreten: ${err.message}`);
            setSubmitting(false);
        }
    };

    const handleDeleteHoliday = async () => {
        try {
            await deleteHoliday({
                variables: { id }
            });
        } catch (err) {
            setError(`Ein Fehler ist aufgetreten: ${err.message}`);
        }
    };

    if (holidayLoading) {
        return <div className="py-8 text-center">Lade Feiertag...</div>;
    }

    if (holidayError) {
        return (
            <div className="py-4">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-800 mb-2">Feiertag nicht gefunden</h2>
                        <p className="text-red-700 mb-4">Der angeforderte Feiertag existiert nicht oder konnte nicht geladen werden.</p>
                        <button onClick={() => navigate('/admin/holidays')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                            Zurück zur Feiertagsverwaltung
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Feiertag bearbeiten</h1>
                    <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="text-sm font-semibold bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded"
                    >
                        Feiertag löschen
                    </button>
                </div>

                <Modal
                    open={isDeleteModalOpen}
                    danger
                    modalHeading="Feiertag wirklich löschen?"
                    primaryButtonText="Löschen"
                    secondaryButtonText="Abbrechen"
                    onRequestClose={() => setIsDeleteModalOpen(false)}
                    onRequestSubmit={handleDeleteHoliday}
                    primaryButtonDisabled={deleteLoading}
                >
                    <p>
                        Wollen Sie den Feiertag <strong>{holidayName}</strong> wirklich löschen? Dies kann nicht rückgängig gemacht werden.
                    </p>
                </Modal>
                
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
                        {error}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    <TextInput
                        id="holiday-name"
                        labelText="Name des Feiertags/der Ferienzeit"
                        placeholder="z.B. Weihnachtsferien, Osterfeiertag"
                        value={holidayName}
                        onChange={(e) => setHolidayName(e.target.value)}
                        required
                    />
                    
                    <div className="mb-4">
                        <p className="mb-2 font-semibold text-gray-700">Zeitraum</p>
                        <HolidayDatePicker 
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates)}
                        />
                    </div>
                    
                    <div className="flex justify-between pt-4">
                        <button 
                            type="button" 
                            onClick={() => navigate('/admin/holidays')}
                            className="px-5 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !formIsValid}
                            className={`mainBtn text-white px-5 py-2 ${(submitting || !formIsValid) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {submitting ? 'Wird aktualisiert...' : 'Aktualisieren'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
